import React, { useState, useEffect } from "react";
import { Box, Cylinder, Circle } from "lucide-react";
import { useModelStore } from "@/store/useModelStore";

// Types
export interface Node {
  id: number;
  x: number;
  y: number;
  z: number;
}

export interface Element {
  id: number;
  start: number;
  end: number;
  type: "beam";
}

export interface Geometry {
  nodes: Node[];
  elements: Element[];
}

interface GeometrySelectorProps {
  onImport: (geometry: Geometry, clearPrevious: boolean) => void;
  onClear?: () => void;
  onClose?: () => void;
}

const GeometrySelector: React.FC<GeometrySelectorProps> = ({
  onImport,
  onClose,
}) => {
  const clearAll = useModelStore((state) => state.clearAll);

  const [isOpen, setIsOpen] = useState(true);
  const [step, setStep] = useState(1);
  const [selectedDesign, setSelectedDesign] = useState<string | null>(null);
  const [clearPrevious, setClearPrevious] = useState(true);

  // geometry properties
  const [totalLength, setTotalLength] = useState(10);
  const [totalWidth, setTotalWidth] = useState(10);
  const [totalHeight, setTotalHeight] = useState(10);

  const [xParts, setXParts] = useState(1);
  const [yParts, setYParts] = useState(1);
  const [zParts, setZParts] = useState(1);

  // custom division arrays
  const [xDivisions, setXDivisions] = useState<number[]>([10]);
  const [yDivisions, setYDivisions] = useState<number[]>([10]);
  const [zDivisions, setZDivisions] = useState<number[]>([10]);

  // state for which custom division modal is open
  const [customAxis, setCustomAxis] = useState<"X" | "Y" | "Z" | null>(null);

  const [startRadius, setStartRadius] = useState(5);
  const [endRadius, setEndRadius] = useState(5);

  const [axisStartX, setAxisStartX] = useState(0);
  const [axisStartY, setAxisStartY] = useState(0);
  const [axisStartZ, setAxisStartZ] = useState(0);

  const [axisEndX, setAxisEndX] = useState(0);
  const [axisEndY, setAxisEndY] = useState(0);
  const [axisEndZ, setAxisEndZ] = useState(10);

  const [sweep, setSweep] = useState(360); // degrees

  // keep divisions array in sync with count
  useEffect(() => {
    setXDivisions(Array(xParts).fill(totalLength / xParts));
  }, [xParts, totalLength]);

  useEffect(() => {
    setYDivisions(Array(yParts).fill(totalWidth / yParts));
  }, [yParts, totalWidth]);

  useEffect(() => {
    setZDivisions(Array(zParts).fill(totalHeight / zParts));
  }, [zParts, totalHeight]);

  // ✅ Grid Generator
  const generateGrid = (): Geometry => {
    let nodes: Node[] = [];
    let elements: Element[] = [];
    let nodeId = 1;
    let elementId = 1;

    const xOffsets: number[] = [0];
    xDivisions.forEach((d) => xOffsets.push(xOffsets[xOffsets.length - 1] + d));

    const yOffsets: number[] = [0];
    yDivisions.forEach((d) => yOffsets.push(yOffsets[yOffsets.length - 1] + d));

    const zOffsets: number[] = [0];
    zDivisions.forEach((d) => zOffsets.push(zOffsets[zOffsets.length - 1] + d));

    for (let xi = 0; xi < xParts; xi++) {
      for (let yi = 0; yi < yParts; yi++) {
        for (let zi = 0; zi < zParts; zi++) {
          const stepX = xDivisions[xi];
          const stepY = yDivisions[yi];
          const stepZ = zDivisions[zi];

          const offsetX = xOffsets[xi];
          const offsetY = yOffsets[yi];
          const offsetZ = zOffsets[zi];

          const cubeNodes: Node[] = [
            { id: nodeId, x: offsetX, y: offsetY, z: offsetZ },
            { id: nodeId + 1, x: offsetX + stepX, y: offsetY, z: offsetZ },
            {
              id: nodeId + 2,
              x: offsetX + stepX,
              y: offsetY,
              z: offsetZ + stepZ,
            },
            { id: nodeId + 3, x: offsetX, y: offsetY, z: offsetZ + stepZ },
            { id: nodeId + 4, x: offsetX, y: offsetY + stepY, z: offsetZ },
            {
              id: nodeId + 5,
              x: offsetX + stepX,
              y: offsetY + stepY,
              z: offsetZ,
            },
            {
              id: nodeId + 6,
              x: offsetX + stepX,
              y: offsetY + stepY,
              z: offsetZ + stepZ,
            },
            {
              id: nodeId + 7,
              x: offsetX,
              y: offsetY + stepY,
              z: offsetZ + stepZ,
            },
          ];

          const cubeElements: Element[] = [
            { id: elementId++, start: nodeId, end: nodeId + 1, type: "beam" },
            {
              id: elementId++,
              start: nodeId + 1,
              end: nodeId + 5,
              type: "beam",
            },
            {
              id: elementId++,
              start: nodeId + 5,
              end: nodeId + 4,
              type: "beam",
            },
            { id: elementId++, start: nodeId + 4, end: nodeId, type: "beam" },
            {
              id: elementId++,
              start: nodeId + 3,
              end: nodeId + 2,
              type: "beam",
            },
            {
              id: elementId++,
              start: nodeId + 2,
              end: nodeId + 6,
              type: "beam",
            },
            {
              id: elementId++,
              start: nodeId + 6,
              end: nodeId + 7,
              type: "beam",
            },
            {
              id: elementId++,
              start: nodeId + 7,
              end: nodeId + 3,
              type: "beam",
            },
            { id: elementId++, start: nodeId, end: nodeId + 3, type: "beam" },
            {
              id: elementId++,
              start: nodeId + 1,
              end: nodeId + 2,
              type: "beam",
            },
            {
              id: elementId++,
              start: nodeId + 5,
              end: nodeId + 6,
              type: "beam",
            },
            {
              id: elementId++,
              start: nodeId + 4,
              end: nodeId + 7,
              type: "beam",
            },
          ];

          nodes = [...nodes, ...cubeNodes];
          elements = [...elements, ...cubeElements];
          nodeId += 8;
        }
      }
    }
    return { nodes, elements };
  };

  const generateCylinder = (): Geometry => {
    let nodes: Node[] = [];
    let elements: Element[] = [];
    let nodeId = 1;
    let elementId = 1;

    const circleSegments = Math.max(3, Math.floor(xParts)); // around
    const verticalSegments = Math.max(1, Math.floor(zParts)); // along axis

    // start / end points (axis)
    const x1 = axisStartX;
    const y1 = axisStartY;
    const z1 = axisStartZ;
    const x2 = axisEndX;
    const y2 = axisEndY;
    const z2 = axisEndZ;

    // axis vector and length
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dz = z2 - z1;
    const axisLen = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-6; // avoid zero

    // unit axis (u)
    const ux = dx / axisLen;
    const uy = dy / axisLen;
    const uz = dz / axisLen;

    // Build orthonormal basis (e1, e2, u)
    // Choose a helper vector not parallel to u
    let hx = 0;
    let hy = 1;
    let hz = 0;
    // if u is very close to helper, use different helper
    if (Math.abs(ux * hx + uy * hy + uz * hz) > 0.999) {
      hx = 1;
      hy = 0;
      hz = 0;
    }

    // e1 = normalize(cross(h, u))
    let e1x = hy * uz - hz * uy;
    let e1y = hz * ux - hx * uz;
    let e1z = hx * uy - hy * ux;
    const e1Len = Math.sqrt(e1x * e1x + e1y * e1y + e1z * e1z) || 1e-6;
    e1x /= e1Len;
    e1y /= e1Len;
    e1z /= e1Len;

    // e2 = cross(u, e1)
    const e2x = uy * e1z - uz * e1y;
    const e2y = uz * e1x - ux * e1z;
    const e2z = ux * e1y - uy * e1x;
    // e2 is already unit because u and e1 are unit and orthogonal (numerical tolerance ok)

    // sweep handling: full circle or partial
    const maxAngle = (Math.min(Math.max(sweep, 0), 360) * Math.PI) / 180;
    const fullCircle = Math.abs(maxAngle - Math.PI * 2) < 1e-6;

    // For full circle: distribute i=0..circleSegments-1, angle = (i / circleSegments) * 2π
    // For partial: distribute i=0..circleSegments-1 across [0, maxAngle] inclusive (so last point at maxAngle)
    for (let z = 0; z <= verticalSegments; z++) {
      const t = z / verticalSegments; // 0..1 along axis
      const radius = startRadius + t * (endRadius - startRadius); // linear taper
      const zLocal = t * axisLen; // local distance along axis

      if (fullCircle) {
        for (let i = 0; i < circleSegments; i++) {
          const angle = (i / circleSegments) * Math.PI * 2;
          const xLocal = radius * Math.cos(angle);
          const yLocal = radius * Math.sin(angle);

          // world = start + e1*xLocal + e2*yLocal + u*zLocal
          const wx = x1 + e1x * xLocal + e2x * yLocal + ux * zLocal;
          const wy = y1 + e1y * xLocal + e2y * yLocal + uy * zLocal;
          const wz = z1 + e1z * xLocal + e2z * yLocal + uz * zLocal;

          nodes.push({ id: nodeId++, x: wx, y: wy, z: wz });
        }
      } else {
        // partial sweep: include end point at maxAngle
        for (let i = 0; i < circleSegments; i++) {
          const denom = Math.max(1, circleSegments - 1);
          const angle = (i / denom) * maxAngle;
          const xLocal = radius * Math.cos(angle);
          const yLocal = radius * Math.sin(angle);

          const wx = x1 + e1x * xLocal + e2x * yLocal + ux * zLocal;
          const wy = y1 + e1y * xLocal + e2y * yLocal + uy * zLocal;
          const wz = z1 + e1z * xLocal + e2z * yLocal + uz * zLocal;

          nodes.push({ id: nodeId++, x: wx, y: wy, z: wz });
        }
      }
    }

    // Connect horizontal ring elements
    for (let z = 0; z <= verticalSegments; z++) {
      const base = z * circleSegments;
      if (fullCircle) {
        // connect every i to (i+1)%circleSegments
        for (let i = 0; i < circleSegments; i++) {
          const start = base + i + 1;
          const end = base + ((i + 1) % circleSegments) + 1;
          elements.push({ id: elementId++, start, end, type: "beam" });
        }
      } else {
        // partial: connect i -> i+1 for i=0..circleSegments-2 (don't close)
        for (let i = 0; i < circleSegments - 1; i++) {
          const start = base + i + 1;
          const end = base + i + 2;
          elements.push({ id: elementId++, start, end, type: "beam" });
        }
      }
    }

    // Connect vertical elements between rings
    for (let z = 0; z < verticalSegments; z++) {
      const base = z * circleSegments;
      const nextBase = (z + 1) * circleSegments;
      for (let i = 0; i < circleSegments; i++) {
        const start = base + i + 1;
        const end = nextBase + i + 1;
        elements.push({ id: elementId++, start, end, type: "beam" });
      }
    }

    return { nodes, elements };
  };

  const handleImport = () => {
    if (clearPrevious) {
      clearAll();
    }
    const geometry =
      selectedDesign === "cylindrical" ? generateCylinder() : generateGrid();
    onImport(geometry, clearPrevious);
    setIsOpen(false);
    setStep(1);
    setSelectedDesign(null);
    if (onClose) onClose();
  };

  return (
    <div>
      {/* Main Popup */}
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="rounded-lg shadow-xl w-[600px] h-[520px] relative dark:bg-gray-800">
            <button
              className="absolute top-2 right-2 text-white hover:text-gray-300"
              onClick={() => {
                setIsOpen(false);
                setStep(1);
                setSelectedDesign(null);
                if (onClose) onClose();
              }}
            >
              ✕
            </button>

            {/* Step 1: Select Design */}
            {step === 1 && (
              <div className="flex flex-col h-full">
                <h2 className="text-white text-lg font-semibold mb-4 p-6 border-b dark:border-gray-600">
                  Select Geometry Design
                </h2>
                <div className="flex flex-col gap-4 p-6 flex-grow overflow-y-auto">
                  {/* Orthogonal */}
                  <div
                    onClick={() => setSelectedDesign("cube")}
                    className={`flex items-center gap-3 p-4 rounded cursor-pointer transition ${
                      selectedDesign === "cube"
                        ? "border border-blue-600"
                        : "hover: border border-gray-400"
                    }`}
                  >
                    <Box size={40} className="text-blue-500" />
                    <div>
                      <p className="font-medium text-white">Orthogonal</p>
                      <p className="text-xs text-white">
                        Right-angled, axis-aligned structure
                      </p>
                    </div>
                  </div>

                  {/* Dummy: Cylindrical / Circular */}
                  <div
                    onClick={() => setSelectedDesign("cylindrical")}
                    className={`flex items-center gap-3 p-4 rounded cursor-pointer transition ${
                      selectedDesign === "cylindrical"
                        ? "border border-blue-600"
                        : "hover: border border-gray-400"
                    }`}
                  >
                    <Cylinder size={40} className="text-blue-500" />
                    <div>
                      <p className="font-medium text-white">
                        Cylindrical / Circular
                      </p>
                      <p className="text-xs text-white">
                        Round or circular geometry, curved surfaces
                      </p>
                    </div>
                  </div>

                  {/* Dummy: Spherical */}
                  <div
                    onClick={() => setSelectedDesign("spherical")}
                    className={`flex items-center gap-3 p-4 rounded cursor-pointer transition ${
                      selectedDesign === "spherical"
                        ? "border border-blue-600"
                        : "hover: border border-gray-400"
                    }`}
                  >
                    <Circle size={40} className="text-blue-500" />
                    <div>
                      <p className="font-medium text-white">Spherical</p>
                      <p className="text-xs text-white">
                        Fully round 3D structure
                      </p>
                    </div>
                  </div>

                  <div className="flex-grow" />
                  <div className="flex justify-end mt-4">
                    <button
                      disabled={
                        !selectedDesign || selectedDesign === "spherical"
                      }
                      className={`px-4 py-2 rounded transition ${
                        selectedDesign && selectedDesign !== "spherical"
                          ? "bg-blue-500 text-white hover:bg-blue-600"
                          : "bg-gray-500 text-gray-300 cursor-not-allowed"
                      }`}
                      onClick={() => setStep(2)}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Properties */}
            {step === 2 && (
              <div className="flex flex-col h-full">
                <h2 className="text-lg font-semibold text-white p-6 border-b dark:border-gray-600">
                  {selectedDesign === "cube"
                    ? "Orthogonal Properties"
                    : "Cylindrical Properties"}
                </h2>
                {selectedDesign === "cube" ? (
                  <>
                    <div className="flex gap-6 flex-grow p-6">
                      {/* Left: Dimensions */}
                      <div className="flex flex-col space-y-3 w-1/2">
                        <div>
                          <label className="block mb-1 font-medium text-blue-400">
                            Width (X):
                          </label>
                          <input
                            type="number"
                            value={totalLength}
                            onChange={(e) =>
                              setTotalLength(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 font-semibold text-white bg-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block mb-1 font-medium text-blue-400">
                            Height (Y):
                          </label>
                          <input
                            type="number"
                            value={totalWidth}
                            onChange={(e) =>
                              setTotalWidth(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 font-semibold text-white bg-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block mb-1 font-medium text-blue-400">
                            Length (Z):
                          </label>
                          <input
                            type="number"
                            value={totalHeight}
                            onChange={(e) =>
                              setTotalHeight(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 font-semibold text-white bg-gray-700"
                          />
                        </div>
                      </div>

                      {/* Right: Divisions */}
                      <div className="flex flex-col space-y-3 w-1/2">
                        {/* X Division */}
                        <div
                          className={`transition-all duration-300 ${
                            totalLength === 0
                              ? "invisible opacity-0"
                              : "visible opacity-100"
                          }`}
                        >
                          <label className="block mb-1 font-medium text-blue-400">
                            Divisions X:
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              min={1}
                              value={xParts}
                              onChange={(e) =>
                                setXParts(Math.max(1, Number(e.target.value)))
                              }
                              className="w-full border rounded px-2 py-1 font-semibold text-white bg-gray-700"
                            />
                            <button
                              className="px-2 rounded bg-blue-500 text-white hover:bg-blue-600"
                              onClick={() => setCustomAxis("X")}
                            >
                              Custom
                            </button>
                          </div>
                        </div>

                        {/* Y Division */}
                        <div
                          className={`transition-all duration-300 ${
                            totalWidth === 0
                              ? "invisible opacity-0"
                              : "visible opacity-100"
                          }`}
                        >
                          <label className="block mb-1 font-medium text-blue-400">
                            Divisions Y:
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              min={1}
                              value={yParts}
                              onChange={(e) =>
                                setYParts(Math.max(1, Number(e.target.value)))
                              }
                              className="w-full border rounded px-2 py-1 font-semibold text-white bg-gray-700"
                            />
                            <button
                              className="px-2 rounded bg-blue-500 text-white hover:bg-blue-600"
                              onClick={() => setCustomAxis("Y")}
                            >
                              Custom
                            </button>
                          </div>
                        </div>

                        {/* Z Division */}
                        <div
                          className={`transition-all duration-300 ${
                            totalHeight === 0
                              ? "invisible opacity-0"
                              : "visible opacity-100"
                          }`}
                        >
                          <label className="block mb-1 font-medium text-blue-400">
                            Divisions Z:
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              min={1}
                              value={zParts}
                              onChange={(e) =>
                                setZParts(Math.max(1, Number(e.target.value)))
                              }
                              className="w-full border rounded px-2 py-1 font-semibold text-white bg-gray-700"
                            />
                            <button
                              className="px-2 rounded bg-blue-500 text-white hover:bg-blue-600"
                              onClick={() => setCustomAxis("Z")}
                            >
                              Custom
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : selectedDesign === "cylindrical" ? (
                  /* ✅ NEW CYLINDER UI */
                  <div className="flex flex-col gap-6 flex-grow p-6 text-white overflow-y-auto">
                    {/* ========================= */}
                    {/*      SECTION 1 : RADIUS   */}
                    {/* ========================= */}
                    <div>
                      <h3 className="text-blue-300 font-semibold mb-3">
                        Radius
                      </h3>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Start Radius */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            Start Radius
                          </label>
                          <input
                            type="number"
                            min={0.1}
                            value={startRadius}
                            onChange={(e) =>
                              setStartRadius(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>

                        {/* End Radius */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            End Radius
                          </label>
                          <input
                            type="number"
                            min={0.1}
                            value={endRadius}
                            onChange={(e) =>
                              setEndRadius(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* ======================================= */}
                    {/*  SECTION 2 : AXIS START / END (X Y Z)   */}
                    {/* ======================================= */}
                    <div>
                      <h3 className="text-blue-300 font-semibold mb-3">
                        Axis Start & End
                      </h3>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Start X */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            Start X
                          </label>
                          <input
                            type="number"
                            value={axisStartX}
                            onChange={(e) =>
                              setAxisStartX(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>

                        {/* End X */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            End X
                          </label>
                          <input
                            type="number"
                            value={axisEndX}
                            onChange={(e) =>
                              setAxisEndX(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>

                        {/* Start Y */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            Start Y
                          </label>
                          <input
                            type="number"
                            value={axisStartY}
                            onChange={(e) =>
                              setAxisStartY(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>

                        {/* End Y */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            End Y
                          </label>
                          <input
                            type="number"
                            value={axisEndY}
                            onChange={(e) =>
                              setAxisEndY(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>

                        {/* Start Z */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            Start Z
                          </label>
                          <input
                            type="number"
                            value={axisStartZ}
                            onChange={(e) =>
                              setAxisStartZ(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>

                        {/* End Z */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            End Z
                          </label>
                          <input
                            type="number"
                            value={axisEndZ}
                            onChange={(e) =>
                              setAxisEndZ(Number(e.target.value))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* ============================== */}
                    {/*      SECTION 3 : OTHER         */}
                    {/* ============================== */}
                    <div>
                      <h3 className="text-blue-300 font-semibold mb-3">
                        Other Properties
                      </h3>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Sweep Angle */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            Sweep Angle (°)
                          </label>
                          <input
                            type="number"
                            min={10}
                            max={360}
                            value={sweep}
                            onChange={(e) => setSweep(Number(e.target.value))}
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>

                        {/* Vertical Divisions */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            Vertical Divisions
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={zParts}
                            onChange={(e) =>
                              setZParts(Math.max(1, Number(e.target.value)))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>

                        {/* Circle Divisions */}
                        <div>
                          <label className="block mb-1 text-sm text-gray-300">
                            Circle Segments
                          </label>
                          <input
                            type="number"
                            min={3}
                            value={xParts}
                            onChange={(e) =>
                              setXParts(Math.max(3, Number(e.target.value)))
                            }
                            className="w-full border rounded px-2 py-1 bg-gray-700 text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex justify-between p-6">
                  <label className="flex items-center gap-2 text-white text-sm">
                    <input
                      type="checkbox"
                      checked={clearPrevious}
                      onChange={(e) => setClearPrevious(e.target.checked)}
                      className="w-4 h-4"
                    />
                    Clear previous geometry
                  </label>

                  <button
                    className="px-4 py-2 bg-green-500 rounded hover:bg-green-600"
                    onClick={() => setStep(1)}
                  >
                    ← Back
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    onClick={handleImport}
                  >
                    Finish ✔
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Custom Division Modal */}
      {customAxis && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-[400px] relative">
            {/* Close button */}
            <button
              className="absolute top-2 right-2 text-white"
              onClick={() => {
                if (customAxis === "X") {
                  setXDivisions(Array(xParts).fill(totalLength / xParts));
                } else if (customAxis === "Y") {
                  setYDivisions(Array(yParts).fill(totalWidth / yParts));
                } else {
                  setZDivisions(Array(zParts).fill(totalHeight / zParts));
                }
                setCustomAxis(null);
              }}
            >
              ✕
            </button>

            <h3 className="text-lg font-bold text-white mb-4">
              Custom Divisions ({customAxis})
            </h3>

            {/* Show total length */}
            <p className="text-gray-300 mb-2">
              Total {customAxis} Length:{" "}
              <span className="font-bold">
                {customAxis === "X"
                  ? totalLength
                  : customAxis === "Y"
                    ? totalWidth
                    : totalHeight}
              </span>
            </p>

            {/* Division inputs */}
            {(customAxis === "X"
              ? xDivisions
              : customAxis === "Y"
                ? yDivisions
                : zDivisions
            ).map((val, i) => (
              <input
                key={i}
                type="number"
                value={val}
                onChange={(e) => {
                  const newVal = Number(e.target.value);
                  if (customAxis === "X") {
                    const newArr = [...xDivisions];
                    newArr[i] = newVal;
                    setXDivisions(newArr);
                  } else if (customAxis === "Y") {
                    const newArr = [...yDivisions];
                    newArr[i] = newVal;
                    setYDivisions(newArr);
                  } else {
                    const newArr = [...zDivisions];
                    newArr[i] = newVal;
                    setZDivisions(newArr);
                  }
                }}
                className="w-full border rounded px-2 py-1 mb-2 font-semibold text-white bg-gray-700"
              />
            ))}

            {/* Sum validation + Apply button */}
            {(() => {
              const target =
                customAxis === "X"
                  ? totalLength
                  : customAxis === "Y"
                    ? totalWidth
                    : totalHeight;

              const sum =
                customAxis === "X"
                  ? xDivisions.reduce((a, b) => a + b, 0)
                  : customAxis === "Y"
                    ? yDivisions.reduce((a, b) => a + b, 0)
                    : zDivisions.reduce((a, b) => a + b, 0);

              return (
                <>
                  <p className="text-sm text-gray-400 mt-2">
                    Total = {sum} (must equal {target})
                  </p>

                  <button
                    disabled={sum !== target}
                    className={`mt-4 px-4 py-2 rounded ${
                      sum === target
                        ? "bg-blue-500 text-white hover:bg-blue-600"
                        : "bg-gray-600 text-gray-400 cursor-not-allowed"
                    }`}
                    onClick={() => setCustomAxis(null)}
                  >
                    Apply
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeometrySelector;
